import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Archive, X, ChevronLeft, Lock } from 'lucide-react';
import { StatsView } from './StatsView';

/**
 * Read-only browser for past seasons. Lists every archived season (collected
 * from players' `seasonStats` maps) and, when one is selected, renders the
 * existing StatsView in history mode — scoped to that season's games and totals,
 * with all editing/import/live affordances disabled. The team's CURRENT season
 * is intentionally NOT listed here; it lives on the live Stats tab.
 */
export const SeasonHistoryModal = ({
  team,
  players,
  isElite,
  onClose,
}: {
  team: any;
  players: any[];
  isElite?: boolean;
  onClose: () => void;
}) => {
  // Collect distinct archived seasons across the roster, most recent first.
  const seasons = useMemo(() => {
    const map = new Map<string, { key: string; season: string; year: number; archivedAt: string }>();
    players.forEach((p: any) => {
      const ss = p?.seasonStats ?? {};
      Object.entries(ss).forEach(([key, v]: [string, any]) => {
        if (!map.has(key)) {
          map.set(key, {
            key,
            season: v?.season ?? key,
            year: v?.year ?? 0,
            archivedAt: v?.archivedAt ?? '',
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.year !== b.year ? b.year - a.year : (b.archivedAt > a.archivedAt ? 1 : -1)
    );
  }, [players]);

  const [selected, setSelected] = useState<{ season: string; year: number } | null>(null);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-surface overflow-y-auto safe-top"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-surface/95 backdrop-blur-sm border-b border-hairline px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selected ? (
              <button onClick={() => setSelected(null)} className="text-ink-2 hover:text-ink">
                <ChevronLeft className="w-5 h-5" />
              </button>
            ) : (
              <Archive className="w-5 h-5 text-accent-ink" />
            )}
            <div>
              <h2 className="text-lg font-black italic uppercase text-ink leading-none">
                {selected ? `${selected.season} ${selected.year}` : 'Season History'}
              </h2>
              {selected && (
                <p className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest mt-1 flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5" /> Past season · read-only
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-2 hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        {!selected ? (
          <div className="max-w-2xl mx-auto p-6">
            {seasons.length === 0 ? (
              <div className="text-center py-20">
                <Archive className="w-10 h-10 text-ink-2 mx-auto mb-4" />
                <p className="text-sm font-black text-ink">No past seasons yet</p>
                <p className="text-[10px] text-ink-2 uppercase tracking-widest mt-2 max-w-xs mx-auto">
                  When you start a new season, your previous one gets archived here: games, stats, and all.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                  Tap a season to review its games and stats
                </p>
                {seasons.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSelected({ season: s.season, year: s.year })}
                    className="w-full bg-canvas border border-hairline rounded-2xl p-5 flex items-center justify-between hover:border-emerald-500 hover:bg-emerald-500/10 transition-all text-left"
                  >
                    <div>
                      <p className="text-sm font-black italic uppercase text-ink">{s.season} {s.year}</p>
                      {s.archivedAt && (
                        <p className="text-[10px] text-ink-2 uppercase tracking-widest mt-0.5">
                          Archived {new Date(s.archivedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <ChevronLeft className="w-5 h-5 text-ink-2 rotate-180" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Read-only banner */}
            <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-6 py-2.5">
              <p className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest text-center">
                Viewing {selected.season} {selected.year}: past season, read-only
              </p>
            </div>
            {/* StatsView in history mode — viewSeason scopes games + disables edits/import */}
            <StatsView
              team={team}
              players={players.filter((p: any) => !p.isGuest)}
              isElite={isElite}
              isHeadCoach={false}
              viewSeason={selected}
            />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
